import { DaVinciViewer } from "../../../components/feature/viewer/DaVinciViewer.js";
import { DaVinciService } from "../DaVinciService.js";
import { getAuth } from "firebase/auth";

import { SEOManager } from "../../../utils/seo-manager.js";
import { ContentService } from "../../../services/content.service.js";
import { PointService } from "../../../services/point.service.js";
import { PurchaseModal } from "../../../components/modals/PurchaseModal.js";

export const DaVinciViewerPage = async (seriesId, episodeId) => {
    // 1. Initial SEO Placeholder
    // Fetch series info ASAP for better OG tags if possible, or update later.
    // Since this is SPA, we rely on the render to update it.

    // [FIX v1.34.37] Force Scroll to Top on Init (Nav Fix)
    window.scrollTo(0, 0);

    // [SANITIZATION] Trim IDs to prevent URL junk
    seriesId = String(seriesId).trim();
    episodeId = String(episodeId).trim();

    // [SANITIZATION] Trim IDs to prevent URL junk
    seriesId = String(seriesId).trim();
    episodeId = String(episodeId).trim();

    // [SECURITY] Client-Side Gatekeeper (RESTORED v1.36.37 - STRICT)
    if (!window.user) {
        window.Modal.toast("로그인이 필요한 서비스입니다.");

        // [UX] Redirect to Series Detail (the "Door") instead of Content
        // User Request: "Don't enter content immediately. Stop at the door."
        const seriesDoorPath = `/series/${seriesId}`;
        sessionStorage.setItem('auth_redirect', seriesDoorPath);

        window.router.navigate('/login');
        return document.createElement('div'); // Stop rendering
    }

    // [SELF-HEALING] Handle Legacy/Invalid '0' ID (Zero-Index Fallback)
    if (episodeId === '0' || episodeId === 0) {
        console.warn("[DaVinci] Detected Invalid EID '0'. Attempting Self-Healing...");
        try {
            const allEps = await DaVinciService.listEpisodes(seriesId);
            if (allEps && allEps.length > 0) {
                // Find first published or just first
                const firstEp = allEps.find(e => e.status === 'published') || allEps[0];
                console.log("[DaVinci] Healing -> Redirecting to:", firstEp.id);
                window.router.navigate(`/series/${seriesId}/episode/${firstEp.id}`, { replace: true });
                return document.createElement('div'); // Stop current render
            } else {
                throw new Error("No episodes available to heal.");
            }
        } catch (healError) {
            console.error("Healing Failed:", healError);
            // Fallthrough to standard error handling
        }
    }

    // [DEBUG] Trace ID flow
    console.log("[DaVinciViewerPage] Init:", { seriesId, episodeId });

    // 1. Container Setup
    const container = document.createElement('div');
    container.style.width = '100vw';
    container.style.minHeight = '100vh';
    container.style.backgroundColor = '#121212';

    // Initial Loading State
    container.innerHTML = `<div class="flex items-center justify-center h-screen text-gray-500 font-serif"><span class="animate-spin mr-3">⏳</span> 작품 로딩 중...</div>`;

    try {
        // [FIX v1.36.33] Explicit Auth Wait
        // Wait for Firebase Auth to settle (prevent race condition after redirect)
        await new Promise((resolve) => {
            const unsubscribe = getAuth().onAuthStateChanged((user) => {
                unsubscribe();
                resolve(user);
            });
            // Fallback timeout just in case
            setTimeout(() => resolve(getAuth().currentUser), 2000);
        });

        // 2. Load Data (Parallel Fetch)
        // We need both Series Metadata (for context) and Episode Data (for content)
        // [FIX] Use listPublicEpisodes to avoid Permission Denied for non-authors
        const [seriesRes, epRes, allEps] = await Promise.all([
            DaVinciService.loadSeries(seriesId),
            DaVinciService.loadEpisode(seriesId, episodeId),
            DaVinciService.listPublicEpisodes(seriesId)
        ]);

        if (!seriesRes.success) throw new Error(`시리즈 로드 실패: ${seriesRes.error}`);
        if (!epRes.success) {
            // [DEBUG] Retry Logic for Auth Propagation
            console.warn("First attempt failed. Retrying...", epRes.error);
            await new Promise(r => setTimeout(r, 1000));
            const retryEp = await DaVinciService.loadEpisode(seriesId, episodeId);
            if (!retryEp.success) {
                throw new Error(retryEp.error || "에피소드를 찾을 수 없습니다.");
            }
            // Recovered
            epRes.data = retryEp.data;
        }

        const series = seriesRes.data;
        const episode = epRes.data;

        // [SEO] Update Meta Tags for DaVinci
        SEOManager.updateEpisodeSEO(series.title, episode.title, "DaVinci User");

        // [SECURITY] Draft Guard
        const user = getAuth().currentUser;
        const isAuthor = user && user.uid === seriesRes.data.authorId;
        // Legacy: If status is undefined, treat as published. Block ONLY if explicitly 'draft'.
        const isDraft = epRes.data.status === 'draft';

        if (isDraft && !isAuthor) {
            container.innerHTML = `
                <div class="flex flex-col items-center justify-center h-screen text-gray-400 font-serif p-6 text-center">
                    <span class="material-symbols-outlined text-4xl mb-4 text-amber-600">lock_clock</span>
                    <h2 class="text-xl mb-2 text-white">작성 중인 에피소드입니다</h2>
                    <p class="text-sm opacity-70 mb-8 max-w-md leading-relaxed">
                        작가님이 열심히 집필 중입니다.<br>
                        발행이 완료되면 감상하실 수 있습니다.
                    </p>
                    <button onclick="window.router.navigate('/series/${seriesId}')" class="px-6 py-2 border border-zinc-700 rounded-full hover:bg-zinc-800 transition-colors text-sm">
                        뒤로 가기
                    </button>
                </div>
            `;
            return container;
        }

        // 3. Navigation Calculation (Next/Prev)
        // [Logic] Drafts should be skipped for readers, but visible for authors.
        // We already have 'isAuthor' from the guard block above.

        // [PHASE 2] Initial Load Payment Gate (The Missing Link)
        // [STRICT v1.36.191] Default Locked Policy
        // Logic: "Limit 3" means Eps 1,2,3 Free. Ep 4 Paid.
        // Formula: isFree = (Order <= Limit)

        // 1. Fetch Global Policy & Determine Status
        // 1. Fetch Global Policy & Determine Status
        const globalSettings = await ContentService.getGlobalSettings();
        // [FIX v1.36.199] Robust Fallback: Handle Null AND Empty String (prevent "" -> 0)
        const val = seriesRes.data.freeLimit;
        const freeLimit = (val !== undefined && val !== null && val !== "")
            ? val
            : (globalSettings.default_free_limit || 3);

        // 2. Check Free Policy (Stable Order)
        // We need the index of the CURRENT episode.
        const currentEpIndex = allEps.findIndex(e => String(e.id) === String(episodeId));
        const currentEp = allEps[currentEpIndex];

        // [FIX v1.36.200] Use Visual Index (List Position) for Consistency
        // Trust "What the User Sees": The 1st item in the list is Index 1.
        // Ignore internal 'order' field which might be unsynced.
        const stableIndexInit = currentEpIndex + 1;
        const safeLimitInit = Number(freeLimit);

        // DEFAULT LOCKED
        let isFreeInit = false;

        // [FIX v1.36.195] Safety Guard: 
        // 1. If Index is 1, IT MUST BE FREE. (Absolute Rule)
        // 2. If freeLimit is logical (>=1), check index.
        if (stableIndexInit === 1) {
            isFreeInit = true;
        } else if (stableIndexInit <= safeLimitInit) {
            isFreeInit = true;
        }

        if (!isFreeInit) {
            console.log("[DaVinci] Default Locked (Init):", { stableIndexInit, safeLimitInit });

            // 3. User & Auth Check
            if (!window.user) {
                window.Modal.toast("로그인이 필요한 유료 에피소드입니다.");
                window.router.navigate('/login');
                return document.createElement('div');
            }

            // 4. Ownership Check
            const isUnlocked = await ContentService.checkEpisodeUnlock(window.user.uid, seriesId, episodeId);

            if (!isUnlocked) {
                console.log("[DaVinci] Content Locked. Triggering Modal.");

                // 5. Dynamic Pricing calculation ...
                const series = seriesRes.data;
                const episode = epRes.data;
                const SURCHARGE = series.isAdult ? 50 : 0;
                const RENT_DAYS = globalSettings.rent_days || 7;
                const typePolicies = globalSettings.prices || {};
                const policy = typePolicies[series.type] || typePolicies['davinci'] || { rent_price: 4, own_price: 8 };

                const baseRent = episode.price || policy.rent_price || 4;
                const baseOwn = policy.own_price || (baseRent * 2);

                const finalRent = baseRent + SURCHARGE;
                const finalOwn = baseOwn + SURCHARGE;

                // 6. Blocking Modal
                container.innerHTML = `<div class="w-full h-screen flex flex-col items-center justify-center bg-black text-white">` +
                    `<div class="text-center p-8"><h2 class="text-2xl font-bold mb-4">🔒 유료 에피소드</h2><p>구매 후 열람할 수 있습니다.</p>` +
                    `<button onclick="window.history.back()" class="mt-4 px-4 py-2 border border-zinc-700 rounded hover:bg-zinc-800 text-sm">뒤로 가기</button>` +
                    `</div></div>`;

                new PurchaseModal({
                    title: "잠금 해제",
                    itemName: `${series.title} - ${episode.title}`,
                    rentPrice: finalRent,
                    rentDays: RENT_DAYS,
                    ownPrice: finalOwn,
                    isAdult: series.isAdult,
                    onConfirm: async (type) => {
                        try {
                            const price = type === 'rent' ? finalRent : finalOwn;
                            await PointService.unlockContent(seriesId, episodeId, type, price, `${series.title} - ${episode.title}`);
                            window.Modal.toast("결제가 완료되었습니다.");
                            window.location.reload();
                        } catch (e) { console.error(e); }
                    },
                    onCancel: () => { window.router.navigate(`/series/${seriesId}`); }
                }).render();

                return container;
            }
        }
        // [Logic] Drafts should be skipped for readers, but visible for authors.
        // We already have 'isAuthor' from the guard block above.

        let navigableEps = allEps;
        if (!isAuthor) {
            // Legacy: status undefined => Published. Filter out explicit drafts.
            navigableEps = allEps.filter(e => e.status !== 'draft');
        }

        // [FIX] Ensure type safety (String vs Number)
        const targetEpId = String(episodeId);
        const currentIndex = navigableEps.findIndex(e => String(e.id) === targetEpId); // Find within navigable context

        console.log("[DaVinciViewerPage] Nav Debug:", { targetEpId, foundIndex: currentIndex, total: navigableEps.length });

        const prevEp = currentIndex > 0 ? navigableEps[currentIndex - 1] : null;
        const nextEp = currentIndex !== -1 && currentIndex < navigableEps.length - 1 ? navigableEps[currentIndex + 1] : null;

        // 4. Data Normalization for Viewer
        // Viewer expects: { blocks, metadata, user, navigation }
        // user is already declared above (Line 34)

        // [SAFETY] Sanitize inputs to prevent "Unsupported field value: undefined" in Firestore
        const safeSeries = seriesRes.data || {};
        const safeEp = epRes.data || {};

        // [CRITICAL] Use ID from DB response if available to ensure validity
        const authoritativeSeriesId = safeSeries.id || seriesId;
        console.log("[DaVinciViewerPage] Authoritative ID:", authoritativeSeriesId);

        const metadata = {
            ...safeSeries,      // Helper fields

            // [CRITICAL] Distinct Titles for Resume Engine & Header
            // Header wants 'title' (Episode Title)
            // History/Likes want 'seriesTitle' (Series Title)
            title: safeEp.title || 'Untitled Episode',
            seriesTitle: safeSeries.title || 'Untitled Series',
            seriesId: authoritativeSeriesId, // Ensure Explicit

            episodeIndex: typeof safeEp.order === 'number' ? safeEp.order : (allEps.findIndex(e => e.id === episodeId) + 1),
            authorNote: safeEp.authorNote || safeEp.metadata?.authorNote || '',
            bgm: safeEp.bgm || safeEp.metadata?.bgm || '', // [FIX] Extract from Metadata if missing at root
            thumbnail: safeSeries.thumbnail || '', // Ensure valid string

            // Stats for Footer (Ensure Numbers)
            stats: {
                viewCount: Number(safeSeries.viewCount || safeSeries.stats?.viewCount || 0),
                likeCount: Number(safeSeries.likeCount || safeSeries.stats?.likeCount || 0),
                ratingAverage: Number(safeSeries.stats?.averageRating || 0),
                ratingCount: Number(safeSeries.stats?.ratingCount || 0)
            }
        };
        const navigation = {
            seriesId: authoritativeSeriesId,
            episodeId: episodeId,
            hasPrev: !!prevEp,
            hasNext: !!nextEp,
            onPrev: () => {
                if (prevEp) window.location.hash = `/series/${seriesId}/episode/${prevEp.id}`;
            },
            onNext: async () => {
                if (nextEp) {
                    // [PHASE 1] Secure Payment Gate (DaVinci)

                    // 1. Fetch Global Policy & Determine Free Limit (Robust)
                    const globalSettings = await ContentService.getGlobalSettings();

                    // [FIX v1.36.192] Robust Free Limit (Null & SnakeCase Guard)
                    // If DB is null or undefined, use Global Default (3).
                    // Safe access to snake_case field if valid in DB
                    const rawLimit = series.freeLimit ?? series.free_limit ?? globalSettings.default_free_limit ?? 3;
                    const freeLimit = Number(rawLimit);
                    const safeFreeLimit = isNaN(freeLimit) ? 3 : freeLimit;

                    // 2. Check Free Policy
                    // [STRICT v1.36.192] Default Locked Policy
                    // Logic: "Limit 3" means Eps 1,2,3 Free. Ep 4 Paid.
                    // Formula: isFree = (Order <= Limit)

                    // Use 'order' if available (Stable), else 'index + 1' (Fluctuating)
                    const nextEpObj = nextEp; // Alias
                    const nextEpIndexCalc = currentIndex + 1; // List index
                    // Safe lookup logic
                    const stableIndexRaw = (nextEpObj && nextEpObj.order !== undefined) ? nextEpObj.order : (nextEpIndexCalc + 1);
                    const stableIndex = Number(stableIndexRaw); // CORE FIX: Explicit Cast

                    // DEFAULT LOCKED
                    let isFree = false;
                    if (!isNaN(stableIndex) && stableIndex <= safeFreeLimit) {
                        isFree = true; // Exception
                    }

                    console.log("[DaVinci] Gate Check (Strict):", { stableIndex, safeFreeLimit, isFree, nextId: nextEp.id });

                    if (isFree) {
                        window.router.navigate(`/series/${seriesId}/episode/${nextEp.id}`);
                        return;
                    }

                    // 3. User & Auth Check
                    if (!window.user) {
                        if (confirm("로그인이 필요합니다. 로그인하시겠습니까?")) window.router.navigate('/login');
                        return;
                    }

                    // 4. Ownership Check
                    const isUnlocked = await ContentService.checkEpisodeUnlock(window.user.uid, seriesId, nextEp.id);
                    if (isUnlocked) {
                        window.router.navigate(`/series/${seriesId}/episode/${nextEp.id}`);
                        return;
                    }

                    // 5. Dynamic Pricing & Adult Surcharge
                    // [FIX v1.36.192] Type Safety & Zero Guard for Prices
                    const policy = globalSettings.prices?.davinci || globalSettings.prices?.cine_novel || { rent_price: 250, own_price: 600 };

                    // Prioritize Episode Price -> Policy Price -> Default 250
                    let baseRent = Number(episode.price ?? policy.rent_price ?? 250);
                    let baseOwn = Number(policy.own_price ?? (baseRent * 2));

                    if (baseRent <= 0 || isNaN(baseRent)) {
                        console.warn("[Payment] Invalid/Zero Rent Price Detected. Forcing Default 250P.", baseRent);
                        baseRent = 250;
                    }
                    if (baseOwn <= 0 || isNaN(baseOwn)) {
                        baseOwn = 600;
                    }

                    const SURCHARGE = 0; // No surcharge for now
                    const finalRent = baseRent + SURCHARGE;
                    const finalOwn = baseOwn + SURCHARGE;

                    // ... (rest of logic)atekeeper Modal
                    new PurchaseModal({
                        title: "다음 체험(Scene) 잠금 해제",
                        itemName: `[Cine-Novel] ${series.title} - ${nextEp.title}`,
                        rentPrice: finalRent,
                        rentDays: RENT_DAYS, // [RENTAL] Pass dynamic days
                        ownPrice: finalOwn,
                        isAdult: series.isAdult,
                        onConfirm: async (type) => {
                            try {
                                const price = type === 'rent' ? finalRent : finalOwn;
                                // [SECURE] Unlock via Server Function
                                await PointService.unlockContent(seriesId, nextEp.id, type, price, `[Cine-Novel] ${series.title} - ${nextEp.title}`);

                                window.Modal.toast("체험 준비 완료. 몰입을 시작합니다.");
                                window.router.navigate(`/series/${seriesId}/episode/${nextEp.id}`);
                            } catch (e) {
                                console.error(e);
                                throw e;
                            }
                        }
                    }).render();
                } else {
                    // End of Series (DaVinci Style)
                    // End of Series (DaVinci Style)
                    if (await window.Modal.confirm("모든 체험이 종료되었습니다.<br>작품 홈으로 이동하시겠습니까?", "체험 종료", "glass")) {
                        window.router.navigate(`/series/${seriesId}`);
                    }
                }
            },
            onBack: () => window.router.navigate(`/series/${seriesId}`)
        };


        // [New] Real-time Translation State
        const { templateTranslationService } = await import('../../../services/TranslationService.js');
        const { UniversalViewer } = await import('../../../components/feature/viewer/UniversalViewer.js'); // [RAPS]

        const originalData = {
            title: metadata.title,
            blocks: safeEp.blocks || [] // Keep reference to original structure
        };

        let viewerInstance = null;

        const onTranslate = async (isTranslated) => {
            if (!viewerInstance) return;

            const btnTranslate = container.querySelector('#btn-translate');

            if (!isTranslated) {
                // [OFF] Revert Text & Hide Overlay
                UniversalViewer.hide();
                viewerInstance.updateContent(originalData.blocks, { title: originalData.title });
                window.Modal.toast("원본 모드로 복귀합니다.");
                return;
            }

            const lang = await window.Modal.showLanguageSelector("en");
            if (!lang) {
                // Canceled
                if (btnTranslate) btnTranslate.click();
                return;
            }

            window.Modal.toast(`${lang.toUpperCase()} 번역 및 이미지 분석 중...`);

            try {
                // 1. [TEXT] Translate Title & Text Blocks
                const trTitlePromise = templateTranslationService.translate(originalData.title, lang);
                const blocksPromise = Promise.all(originalData.blocks.map(async (block) => {
                    if (block.type !== 'text') return block;

                    const newBlock = { ...block };
                    newBlock.content = await templateTranslationService.translate(newBlock.content, lang);
                    return newBlock;
                }));

                // Execute Parallel (Text Logic)
                const [trTitle, newBlocks] = await Promise.all([trTitlePromise, blocksPromise]);

                // 3. Update Viewer Text (DOM Re-render)
                // [CRITICAL FIX] Must update DOM *BEFORE* attaching overlays
                viewerInstance.updateContent(newBlocks, { title: trTitle });

                // 2. [IMAGE] Trigger Universal Overlay (RAPS)
                // [FIX] Use Authoritative IDs (Firestore Validated)
                const targetSeriesId = authoritativeSeriesId || seriesId;
                const targetEpisodeId = safeEp.id || episodeId;

                // [FIX] Self-Healing: Handle Missing Data
                // Result is now { success, matched, total }
                let overlayResult = await UniversalViewer.show({
                    seriesId: targetSeriesId,
                    episodeId: targetEpisodeId,
                    lang: lang,
                    mode: 'davinci'
                });

                // Case 1: Absolutely No Data (success: false usually comes from snapshot.empty)
                if (!overlayResult || !overlayResult.success) {
                    // [SELF-HEALING] Data missing? Trigger On-Demand Analysis
                    window.Modal.toast("이미지 분석 데이터가 없습니다.<br>실시간 분석을 시작합니다. (약 10-20초 소요)");

                    try {
                        const { getFunctions, httpsCallable } = await import("firebase/functions");
                        // App is not in scope, use getApp or clean import
                        const { app } = await import("../../../firebase-config.js");
                        const functions = getFunctions(app);
                        const triggerOCR = httpsCallable(functions, 'manuallyTriggerOCR');

                        // Trigger Header-Level Analysis
                        const result = await triggerOCR({
                            mode: 'davinci',
                            seriesId: targetSeriesId,
                            episodeId: targetEpisodeId,
                            uid: seriesRes.data.authorId || window.user.uid // Use Author ID if available (should be)
                        });

                        // Retry Overlay
                        if (result.data && result.data.processed > 0) {
                            window.Modal.toast("분석 완료! 번역 레이어를 생성합니다.");
                            overlayResult = await UniversalViewer.show({
                                seriesId: targetSeriesId,
                                episodeId: targetEpisodeId,
                                lang: lang,
                                mode: 'davinci'
                            });
                        } else {
                            // Still no data (maybe no images?)
                            console.warn("Self-Healing returned 0 results.", result.data);
                        }

                    } catch (healingError) {
                        console.error("Self-Healing Failed:", healingError);
                        window.Modal.toast("실시간 분석 실패. 텍스트 번역만 표시됩니다.");
                    }
                }

                // Case 2: Partial Success (Matched < Total)
                if (overlayResult && overlayResult.success && overlayResult.matched < overlayResult.total) {
                    console.warn(`[DaVinci] Partial Overlay: ${overlayResult.matched}/${overlayResult.total}`);
                    const missing = overlayResult.total - overlayResult.matched;

                    // [AUTO-HEAL] If mismatches exist, automatically trigger re-analysis without blocking popup
                    // User reported popup not appearing, so we force the repair.
                    window.Modal.toast(`누락된 이미지 ${missing}장 감지. 자동 복구(Re-analysis)를 시작합니다...`);

                    try {
                        const { getFunctions, httpsCallable } = await import("firebase/functions");
                        const { app } = await import("../../../firebase-config.js");
                        const functions = getFunctions(app);
                        const triggerOCR = httpsCallable(functions, 'manuallyTriggerOCR');

                        await triggerOCR({
                            mode: 'davinci',
                            seriesId: targetSeriesId,
                            episodeId: targetEpisodeId,
                            uid: seriesRes.data.authorId || window.user.uid
                        });

                        window.Modal.toast("복구 완료! 화면을 갱신합니다.");
                        // Retry Overlay
                        await UniversalViewer.show({
                            seriesId: targetSeriesId,
                            episodeId: targetEpisodeId,
                            lang: lang,
                            mode: 'davinci'
                        });

                    } catch (e) {
                        console.error("Auto-heal failed:", e);
                        window.Modal.toast("자동 복구 실패: " + e.message);
                    }
                }

                if (overlayResult && overlayResult.success) {
                    window.Modal.toast(`번역 완료 (이미지 ${overlayResult.matched}/${overlayResult.total})`);
                } else {
                    window.Modal.toast("텍스트 번역이 완료되었습니다. (이미지 제외)");
                }

            } catch (e) {
                console.error("DaVinci Translation Failed", e);
                window.Modal.alert("번역 실패: " + e.message);
                // Revert visual
                if (btnTranslate) btnTranslate.click();
                UniversalViewer.hide(); // cleanup
            }
        };

        // 5. Render Full Viewer
        container.innerHTML = ''; // Clear loading state

        // DaVinciViewer is an Async Component factory
        viewerInstance = await DaVinciViewer(container, {
            blocks: safeEp.blocks || [],
            metadata,
            user,
            navigation,
            onTranslate: onTranslate // [New] Pass handler
        });

        // 6. Cleanup Hook
        container.destroy = () => {
            if (viewerInstance && viewerInstance.destroy) viewerInstance.destroy();
        };

        // [FACTORY PATTERN] Trigger Copyright Police (Unified)
        // 1. Fetch Global Settings
        import('../../../services/admin-popup.service.js').then(async ({ AdminPopupService }) => {
            try {
                // [Refactor] Use Centralized Resolution (Auto-Language Support)
                const globalSettings = await AdminPopupService.getResolvedCopyrightSettings();

                // 2. Prepare Config (Merge Global w/ DaVinci Specifics if needed)
                let finalConfig = {};

                if (globalSettings.mode === 'custom') {
                    finalConfig = {
                        title: globalSettings.title,
                        theme: globalSettings.theme,
                        legalText: globalSettings.legalText,
                        buttonText: globalSettings.buttonText,
                        // Icon mapping based on theme
                        icon: globalSettings.theme === 'police' ? '👮‍♂️' : (globalSettings.theme === 'horror' ? '👻' : '⚠️')
                    };
                } else {
                    // Default DaVinci Mode (Police Theme)
                    // [FIX] Consistently apply DaVinci Defaults even if resolution happened
                    finalConfig = {
                        title: "동작 그만! 캡처냐?",
                        icon: "👮‍♂️",
                        buttonText: "아닙니다! 눈으로만 보겠습니다!",
                        legalText: `
                            <p><strong>[저작권법 위반 경고]</strong></p>
                            <p>본 작품은 <strong>저작권법 제136조</strong>의 보호를 받습니다.</p>
                            <p class="text-red-400 font-bold border-l-4 border-red-500 pl-2 my-2">
                                 무단 캡처/유포 시<br>
                                 5년 이하의 징역 또는<br>
                                 5천만 원 이하의 벌금
                            </p>
                        `,
                        theme: 'police'
                    };
                }

                // 3. Trigger Warning
                import('../../../components/feature/viewer/CopyrightWarning.js').then(({ CopyrightWarning }) => {
                    CopyrightWarning(container, finalConfig);
                });

            } catch (e) {
                console.warn("[DaVinciViewer] Failed to load copyright settings:", e);
                // Fallback
                import('../../../components/feature/viewer/CopyrightWarning.js').then(({ CopyrightWarning }) => {
                    CopyrightWarning(container);
                });
            }
        });

    } catch (e) {
        console.error("DaVinci Load Error:", e);
        container.innerHTML = `
            <div class="flex flex-col items-center justify-center h-screen text-red-400 font-serif p-4 text-center">
                <span class="material-symbols-outlined text-4xl mb-4">broken_image</span>
                <h2 class="text-xl mb-2">작품을 불러올 수 없습니다</h2>
                <p class="text-sm opacity-70 mb-4 bg-zinc-900 p-2 rounded max-w-lg break-all border border-zinc-800">
                    오류: ${e.message}<br>
                    <span class="text-xs text-zinc-500 mt-2 block">
                        PID: ${seriesId} | EID: ${episodeId}<br>
                        UID: ${window.user ? window.user.uid.slice(0, 5) + '...' : 'Guest'}
                    </span>
                </p>
                <button onclick="window.history.back()" class="px-6 py-2 border border-zinc-700 rounded-full hover:bg-zinc-800 transition-colors">
                    뒤로 가기
                </button>
            </div>
        `;
    }

    return container;
};
